import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { Button, ScrollView, useTheme, YStack, Spinner } from 'tamagui';
import { useEventDetail } from '../../hooks/useEventDetail';

import { CobuContainer, CobuStack, CobuText, EventDetailSkeleton, CobuSpacer, CobuButton, CobuHeader, CobuBackButton } from '@cobu/ui';
import { Strings } from '../../constants/Strings';
import { EventDetailHeader } from '../../components/events/EventDetailHeader';
import { EventDetailInfo } from '../../components/events/EventDetailInfo';
import { EventDetailFooter } from '../../components/events/EventDetailFooter';
import GroupDiscussion from '../../components/chat/groupDiscussion/GroupDiscussion';
import AllMembersModal from '../../components/ui/AllMembersModal';
import { ReportUserModal } from '../../components/ui/ReportUserModal';
// import { GroupDiscussion } from '../../components/GroupDiscussion';
import { CommentType } from '@cobu/types';
import { BW_GROUPS } from '../../constants/user';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
// import { Ionicons } from '@expo/vector-icons';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import { showErrorMessage } from '../../utils/messageToast';
import { useResidentRestrictions } from '../../hooks/useResidentRestrictions';

const strings = Strings.get();

export default function EventDetailScreen() {
  const theme = useTheme();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const eventId = params.id || '';
  const { canPost, canReport } = useResidentRestrictions();
  // Get eventDate from URL params to ensure timing matches between list and detail pages
  const currentEventDate = searchParams.get('eventDate') || '';

  // Deep linking parameters
  const commentId = searchParams.get('commentId') || undefined;
  const scrollToComment = searchParams.get('scrollToComment') === 'true';
  const deepLinkSource = searchParams.get('source') || undefined;

  // Deep linking loading state
  const [isFindingDeepLinkedComment, setIsFindingDeepLinkedComment] = useState(false);

  const {
    // Data
    gathering,
    rsvps,
    currentMembers,
    currentUser,
    eventDate: formattedEventDate,

    // States
    isLoading,
    error,
    userIsGroupMember,
    isCurrentUserCreator,
    isAllMembersModalVisible,
    isReportModalVisible,
    isRsvpActionPending,
    isParentDetailsLoading,
    rsvpObj,

    // Computed values
    totalAttendance,
    rsvpTotalCapacity,

    // Handlers
    handleRSVPPress,
    handleUpdateRSVP,
    handleCancelRSVP,
    handleCommentSubmit,
    handleDeleteComment,
    handleCancelGathering,
    handleWhosComingPress,
    handleCloseAllMembersModal,
    handleReportPress,
    handleCloseReportModal,
    handleBlockUserPress,
    handleClose,
    handleRetry,
    refreshData,
    handleComposeComment,
  } = useEventDetail({ eventId, currentEventDate });

  // All data, state, and handlers come from useEventDetail

  useEffect(() => {
    analyticsService.trackEvent(AnalyticsEvent.VIEW_GROUP_EVENT_DETAIL, {
      uuid: gathering?.uuid,
      title: gathering?.title,
      groupId: gathering?.groupId,
      groupName: gathering?.groupName,
      scheduledStartTime: gathering?.scheduledStartTime,
    });
  }, [gathering]);

  const hasShownErrorRef = useRef(false);
  const hasScrolledToCommentsRef = useRef(false);
  useEffect(() => {
    if (error && !hasShownErrorRef.current) {
      showErrorMessage(strings.common.somethingWentWrong);
      hasShownErrorRef.current = true;
    }
  }, [error]);

  useEffect(() => {
    // After the event has fully loaded, handle hash-based deep links like #comments.
    // We also delay slightly so the ScrollView content is laid out before scrolling.
    if (!gathering) return;
    if (typeof window === 'undefined') return;
    if (hasScrolledToCommentsRef.current) return;
    if (location.hash !== '#comments') return;

    const timeoutId = window.setTimeout(() => {
      const el = document.getElementById('comments');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        hasScrolledToCommentsRef.current = true;
      }
    }, 1000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [gathering, location.hash]);

  const renderCommentsHeader = () => {
    const showCommentsHeader =
      userIsGroupMember ||
      BW_GROUPS.includes(gathering.groupName || '') ||
      gathering.groupName === currentUser?.building?.name;

    if (!showCommentsHeader) {
      return null;
    }

    return (
      <CobuStack id="comments" gap="$3" mt="$4" px="$4" pb="$4">
        <CobuStack layout="horizontal" ai="center" jc="space-between">
          <CobuText variant="heading" fos="$5" fow="bold" col="$cobuDarkBlue" fs={1}>
            {strings.events.comments.title}
          </CobuText>

          {canPost && (
            <CobuButton dsVariant='tertiary' br="$6" onPress={() => handleComposeComment()} fs={1} title={strings.events.comments.addComment} iconLeft={<ChatBubbleOutlineIcon htmlColor={theme.buttonText?.val as string} />} />
          )}
        </CobuStack>
      </CobuStack>
    );
  };

  const renderDeepLinkFindingOverlay = useCallback(() => {
    if (!isFindingDeepLinkedComment) return null;
    return (
      <YStack
        ai="center"
        jc="center"
        bc="$blackTransparent50"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }}
      >
        <YStack
          ai="center"
          jc="center"
          gap="$2"
          p="$4"
          br="$4"
          bc="$white"
          maw={280}
          mx="$4"
        >
          <Spinner size="small" color="#00B4D8" />
          <CobuText variant="body" color="$cobuDarkBlue" fos="$3" ta="center">
            {Strings.get().events.detail.labels.findingComment}
          </CobuText>
        </YStack>
      </YStack>
    );
  }, [isFindingDeepLinkedComment]);

  if (isLoading) {
    return <EventDetailSkeleton />;
  }

  if (error || !gathering) {
    return null;
  }

  return (
    <CobuContainer f={1} bg="$white">
      <CobuHeader title={gathering?.title || strings.events.detail.title} renderLeft={() => <CobuBackButton onPress={() => navigate(-1)} />} />

      <ScrollView showsVerticalScrollIndicator={false}>
        <EventDetailHeader
          gathering={gathering}
          rsvps={rsvps}
          currentUser={currentUser}
          isCurrentUserCreator={isCurrentUserCreator}
          totalAttendance={totalAttendance}
          onWhosComingPress={() => {
            /* ANALYTICS_DISABLED: VIEW_WHOS_COMING_TO_EVENT ('view_whos_coming_to_event')
              analyticsService.trackEvent(AnalyticsEvent.VIEW_WHOS_COMING_TO_EVENT);
              */
            handleWhosComingPress();
          }}
          onEditPress={() => {
            const params = new URLSearchParams({
              editMode: 'true',
              eventId: gathering.uuid,
              title: gathering.title || '',
              description: gathering.description || '',
              locationString: gathering.locationString || '',
              whatToBring: gathering.whatToBring || '',
              maxEventAttendees: (gathering.maxEventAttendees ?? '').toString(),
              maxGuests: (gathering.maxGuests ?? 0).toString(),
              startTime: gathering.scheduledStartTime || '',
              groupId: gathering.groupId || '',
              groupName: gathering.groupName || '',
              imageUri: gathering.image || '',
              currentEventDate: formattedEventDate || '',
            });
            navigate(`/events/create-group-event?${params.toString()}`);
          }}
          onCancelGatheringPress={handleCancelGathering}
          onBlockUserPress={handleBlockUserPress}
          onReportPress={handleReportPress}
          canReport={canReport}
        />

        <EventDetailInfo gathering={gathering} currentMembers={currentMembers} />

        {renderCommentsHeader()}

        <GroupDiscussion
          commentType={CommentType.GroupEventComment}
          groupEventId={gathering.uuid}
          eventDate={formattedEventDate}
          targetCommentId={commentId}
          scrollToComment={scrollToComment}
          skipInitialDeepLinkRefresh={deepLinkSource === 'notification'}
          onDeepLinkLoadingChange={setIsFindingDeepLinkedComment}
          onComposeComment={handleComposeComment}
          onUserProfile={(userId: string) => navigate(`/public-profile/${userId}`)}
          virtualize={false}
          emptyState={<></>}
        />
        <CobuSpacer size="$24" />
      </ScrollView>

      {/* Deep Link Finding Overlay */}
      {renderDeepLinkFindingOverlay()}

      <AllMembersModal
        visible={isAllMembersModalVisible}
        onClose={handleCloseAllMembersModal}
        rsvps={rsvps}
        eventTitle={gathering?.title || strings.events.detail.fallbackTitle}
        totalAttendance={totalAttendance}
      />


      {gathering?.creator?.uuid && currentUser?.uuid && isReportModalVisible && (
        <ReportUserModal
          visible={isReportModalVisible}
          onClose={handleCloseReportModal}
          reportedUserUuid={gathering.creator.uuid}
          reportedByUuid={currentUser.uuid}
          gathering={gathering}
        />
      )}

      <EventDetailFooter
        gathering={gathering}
        rsvpObj={rsvpObj}
        currentUser={currentUser}
        totalAttendance={totalAttendance}
        rsvpTotalCapacity={rsvpTotalCapacity}
        onRSVPPress={handleRSVPPress}
        onCancelRSVP={handleCancelRSVP}
        onUpdateRSVP={handleUpdateRSVP}
        isRsvpActionPending={isRsvpActionPending}
        isParentDetailsLoading={isParentDetailsLoading}
        onClose={handleClose}
      />
    </CobuContainer>
  );
} 